// ... existing code ...
import { useState, FormEvent, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { bookingPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

interface FormErrors {
  name?: string;
  phone?: string;
  age?: string;
  concern?: string;
}

interface FormTouched {
  name?: boolean;
  phone?: boolean;
  age?: boolean;
  concern?: boolean;
}

export default function Booking() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<FormErrors>();
  const [touched, setTouched] = useState<FormTouched>();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    age: "",
    concern: "",
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [submitted]);

  const validateField = useCallback((field: keyof FormErrors, value: string) => {
    switch (field) {
      case "name":
        if (!value.trim()) return "請輸入姓名";
        if (value.trim().length < 2) return "姓名至少需要 2 個字";
        return undefined;
      case "phone":
        if (!value.trim()) return "請輸入手機號碼";
        if (!/^[0-9\-\+\s]{7,15}$/.test(value.trim())) return "請輸入有效的手機號碼格式";
        return undefined;
      case "age":
        if (value && (Number(value) < 1 || Number(value) > 120)) return "請輸入有效的年齡（1–120）";
        return undefined;
      case "concern":
        if (value && value.length > 500) return "內容不可超過 500 字";
        return undefined;
      default:
        return undefined;
    }
  }, []);

  const validateAll = useCallback(() => {
    const newErrors: FormErrors = {};
    newErrors.name = validateField("name", formData.name);
    newErrors.phone = validateField("phone", formData.phone);
    newErrors.age = validateField("age", formData.age);
    newErrors.concern = validateField("concern", formData.concern);
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  }, [formData, validateField]);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (touched[field as keyof FormTouched]) {
      setErrors((prev) => ({ ...prev, [field]: validateField(field as keyof FormErrors, value) }));
    }
  };

  const handleBlur = (field: keyof FormTouched & keyof typeof formData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field as keyof FormErrors, formData[field]) }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouched({ name: true, phone: true, age: true, concern: true });
    if (!validateAll()) {
      setError("請修正表單中的錯誤後再送出");
      return;
    }
    setError("");
    setSubmitting(true);
    const form = e.currentTarget;
    const formDataObj = new FormData(form);
    try {
      const response = await fetch("https://readdy.ai/api/form/d7tiqnfb5ro7pimqhpkg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formDataObj as any).toString(),
      });
      if (response.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setError("提交失敗，請稍後再試或透過 LINE 聯繫我們。");
      }
    } catch {
      setError("網路錯誤，請稍後再試或透過 LINE 聯繫我們。");
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full bg-abyss-900 border rounded-lg px-4 py-3 text-ghost text-base focus:outline-none focus:ring-1 transition-colors";
  const inputNormal = "border-cyber-gold/20 focus:border-cyber-gold/50 focus:ring-cyber-gold/20";
  const inputError = "border-red-400/60 focus:border-red-400/80 focus:ring-red-400/20";
  const inputSuccess = "border-green-400/40 focus:border-green-400/60 focus:ring-green-400/20";

  const getInputClass = (field: keyof FormErrors) => {
    if (touched[field] && errors[field]) return `${inputBase} ${inputError}`;
    if (touched[field] && !errors[field]) return `${inputBase} ${inputSuccess}`;
    return `${inputBase} ${inputNormal}`;
  };

  return (
    <>
      <SEO
        title="線上預約逆齡評估｜時光逆行駭客｜曾驛翔院長門診諮詢"
        description="線上預約時光逆行駭客門診：逆齡評估、男性懸崖式衰老、青春痘與膚況、體態管理、熟齡與長青族慢老諮詢。填寫表單後將由專人與您聯繫確認時段。"
        keywords="預約逆齡評估,曾驛翔門診,線上預約,醫學美容諮詢,逆齡門診"
        canonicalPath="/booking"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "線上預約逆齡評估｜時光逆行駭客",
          "description": "線上預約時光逆行駭客門診諮詢與逆齡評估。",
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">{bookingPageData.heroTagline}</span>
            <h1 className="mt-4 text-3xl md:text-5xl font-bold text-ghost leading-tight">
              {bookingPageData.heroTitle}
              <br className="hidden md:block" />
              <span className="text-gradient-gold">{bookingPageData.heroTitleAccent}</span>
            </h1>
            <p className="mt-6 text-ghost-muted text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              {bookingPageData.heroDesc}
            </p>
          </div>
        </div>
      </section>

      {/* Form + Info Card */}
      <section className="py-8 md:py-16">
        <div
          className="mx-auto px-4 md:px-6 lg:px-8"
          style={{ maxWidth: 1280 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="card-cyber p-8 md:p-12 text-center reveal">
                  <div className="w-16 h-16 rounded-full bg-neon-teal/10 flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-neon-teal text-2xl" />
                  </div>
                  <h2 className="text-ghost font-bold text-2xl mb-3">
                    {bookingPageData.success.title}
                  </h2>
                  <p className="text-ghost-muted text-base leading-relaxed mb-8 max-w-lg mx-auto">
                    {bookingPageData.success.desc}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                      href={lineUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-line"
                    >
                      <i className="ri-line-fill" />
                      {bookingPageData.success.ctaLine}
                    </a>
                    <Link to="/" className="btn-gold-outline">
                      {bookingPageData.success.ctaHome}
                    </Link>
                  </div>
                </div>
              ) : (
                <form
                  id="booking-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="card-cyber p-6 md:p-10 reveal"
                >
                  <div className="space-y-7">
                    {/* Name */}
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-ghost text-base font-medium mb-2"
                      >
                        {bookingPageData.formLabels.name}{" "}
                        <span className="text-cyber-gold">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        onBlur={() => handleBlur("name")}
                        className={getInputClass("name")}
                        placeholder="請輸入您的姓名"
                      />
                      {touched.name && errors.name && (
                        <p className="mt-1.5 flex items-center gap-1.5 text-red-400 text-base">
                          <i className="ri-error-warning-line" />
                          {errors.name}
                        </p>
                      )}
                      {touched.name && !errors.name && formData.name && (
                        <p className="mt-1.5 flex items-center gap-1.5 text-green-400 text-base">
                          <i className="ri-check-line" />
                          格式正確
                        </p>
                      )}
                    </div>

                    {/* Gender + Age row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-ghost text-base font-medium mb-2">
                          {bookingPageData.formLabels.gender}
                        </label>
                        <div className="flex flex-wrap gap-4">
                          {bookingPageData.genderOptions.map((opt) => (
                            <label
                              key={opt.value}
                              className="inline-flex items-center gap-2 text-ghost-muted text-base cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="gender"
                                value={opt.value}
                                className="accent-cyber-gold w-4 h-4"
                              />
                              {opt.label}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="age"
                          className="block text-ghost text-base font-medium mb-2"
                        >
                          {bookingPageData.formLabels.age}
                        </label>
                        <input
                          id="age"
                          name="age"
                          type="number"
                          min="1"
                          max="120"
                          value={formData.age}
                          onChange={(e) => handleInputChange("age", e.target.value)}
                          onBlur={() => handleBlur("age")}
                          className={getInputClass("age")}
                          placeholder="請輸入年齡"
                        />
                        {touched.age && errors.age && (
                          <p className="mt-1.5 flex items-center gap-1.5 text-red-400 text-base">
                            <i className="ri-error-warning-line" />
                            {errors.age}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Phone + LINE ID row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-ghost text-base font-medium mb-2"
                        >
                          {bookingPageData.formLabels.phone}{" "}
                          <span className="text-cyber-gold">*</span>
                        </label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          onBlur={() => handleBlur("phone")}
                          className={getInputClass("phone")}
                          placeholder="請輸入手機號碼"
                        />
                        {touched.phone && errors.phone && (
                          <p className="mt-1.5 flex items-center gap-1.5 text-red-400 text-base">
                            <i className="ri-error-warning-line" />
                            {errors.phone}
                          </p>
                        )}
                        {touched.phone && !errors.phone && formData.phone && (
                          <p className="mt-1.5 flex items-center gap-1.5 text-green-400 text-base">
                            <i className="ri-check-line" />
                            格式正確
                          </p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="line_id"
                          className="block text-ghost text-base font-medium mb-2"
                        >
                          {bookingPageData.formLabels.lineId}
                        </label>
                        <input
                          id="line_id"
                          name="line_id"
                          type="text"
                          className={`${inputBase} ${inputNormal}`}
                          placeholder="請輸入 LINE ID"
                        />
                      </div>
                    </div>

                    {/* Topics */}
                    <div>
                      <label className="block text-ghost text-base font-medium mb-3">
                        {bookingPageData.formLabels.topics}
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {bookingPageData.topicOptions.map((opt) => (
                          <label
                            key={opt.value}
                            className="inline-flex items-center gap-2.5 text-ghost-muted text-base cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              name="topics"
                              value={opt.value}
                              className="accent-cyber-gold w-4 h-4"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Concern */}
                    <div>
                      <label
                        htmlFor="concern"
                        className="block text-ghost text-base font-medium mb-2"
                      >
                        {bookingPageData.formLabels.concern}
                      </label>
                      <textarea
                        id="concern"
                        name="concern"
                        rows={4}
                        maxLength={500}
                        value={formData.concern}
                        onChange={(e) => handleInputChange("concern", e.target.value)}
                        onBlur={() => handleBlur("concern")}
                        className={getInputClass("concern")}
                        placeholder="請簡述您目前最困擾的問題（最多500字）"
                      />
                      <div className="flex items-center justify-between mt-1.5">
                        {touched.concern && errors.concern ? (
                          <p className="flex items-center gap-1.5 text-red-400 text-base">
                            <i className="ri-error-warning-line" />
                            {errors.concern}
                          </p>
                        ) : (
                          <span />
                        )}
                        <p className={`text-base ${formData.concern.length > 500 ? "text-red-400" : "text-ghost-dim"}`}>
                          {formData.concern.length} / 500 字
                        </p>
                      </div>
                    </div>

                    {/* Contact Method */}
                    <div>
                      <label className="block text-ghost text-base font-medium mb-2">
                        {bookingPageData.formLabels.contactMethod}
                      </label>
                      <div className="flex flex-wrap gap-4">
                        {bookingPageData.contactMethodOptions.map((opt) => (
                          <label
                            key={opt.value}
                            className="inline-flex items-center gap-2 text-ghost-muted text-base cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="contact_method"
                              value={opt.value}
                              className="accent-cyber-gold w-4 h-4"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Contact Time */}
                    <div>
                      <label className="block text-ghost text-base font-medium mb-2">
                        {bookingPageData.formLabels.contactTime}
                      </label>
                      <div className="flex flex-wrap gap-4">
                        {bookingPageData.contactTimeOptions.map((opt) => (
                          <label
                            key={opt.value}
                            className="inline-flex items-center gap-2 text-ghost-muted text-base cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="contact_time"
                              value={opt.value}
                              className="accent-cyber-gold w-4 h-4"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Has History */}
                    <div>
                      <label className="block text-ghost text-base font-medium mb-2">
                        {bookingPageData.formLabels.hasHistory}
                      </label>
                      <div className="flex flex-wrap gap-4">
                        {bookingPageData.historyOptions.map((opt) => (
                          <label
                            key={opt.value}
                            className="inline-flex items-center gap-2 text-ghost-muted text-base cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="has_history"
                              value={opt.value}
                              className="accent-cyber-gold w-4 h-4"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Note */}
                    <div className="bg-abyss-900/60 border border-abyss-700 rounded-lg p-4">
                      <p className="text-ghost-dim text-base leading-relaxed">
                        {bookingPageData.formLabels.note}
                      </p>
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 text-red-400 text-base">
                        <i className="ri-error-warning-line" />
                        {error}
                      </div>
                    )}

                    {/* Submit */}
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="btn-gold w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? (
                          <>
                            <i className="ri-loader-4-line animate-spin" />
                            {bookingPageData.formLabels.submitting}
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line" />
                            {bookingPageData.formLabels.submit}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>

            {/* Info Card */}
            <div className="lg:col-span-1">
              <div className="card-cyber p-6 md:p-8 sticky top-28 reveal">
                <div className="flex items-center gap-2.5 mb-5">
                  <i className="ri-error-warning-line text-neon-cyan text-xl" />
                  <h3 className="text-ghost font-bold text-lg">
                    {bookingPageData.infoCard.title}
                  </h3>
                </div>
                <ul className="flex flex-col gap-4">
                  {bookingPageData.infoCard.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-neon-cyan/10 flex items-center justify-center text-neon-cyan text-base font-bold mt-0.5">
                        {idx + 1}
                      </span>
                      <span className="text-ghost-muted text-base leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* LINE Quick Contact */}
                <div className="mt-8 pt-6 border-t border-abyss-700">
                  <span className="section-label text-base">
                    {bookingPageData.lineSection.label}
                  </span>
                  <h4 className="text-ghost font-bold text-lg mt-2 mb-1">
                    {bookingPageData.lineSection.title}
                  </h4>
                  <p className="text-ghost-dim text-base mb-4">
                    LINE ID：{bookingPageData.lineSection.lineId}
                  </p>
                  <a
                    href={lineUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-line w-full justify-center"
                  >
                    <i className="ri-line-fill" />
                    {bookingPageData.lineSection.cta}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}
